/**
 * 可折叠项目列表组件
 * 适合移动端使用，默认折叠显示关键信息，展开显示详情
 * 设计理念: 信息层级清晰、折叠交互自然、状态色彩统一
 */
import React, { useState } from 'react';
import { Collapse, Progress, Tag, Space, Button, Typography, Divider, Tooltip } from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  RightOutlined,
  ExportOutlined,
  PlusOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { DashboardService } from '../../services/dashboardService';
import { getBudgetBasedColors } from '../../utils/colorUtils';

const { Text } = Typography;
const { Panel } = Collapse;

// 统一配色系统
const LIST_THEME = {
  background: 'rgba(224, 230, 234, 0.86)',
  border: '1px solid rgba(16, 27, 35, 0.08)',
  panelBg: 'rgba(232, 237, 240, 0.9)',
  panelBorder: '1px solid rgba(16, 27, 35, 0.08)',
  headerBg: 'rgba(220, 227, 232, 0.78)',
  textPrimary: '#10171d',
  textSecondary: '#455763',
  textMuted: '#667986',
  accentGold: '#b99258',
  accentBlue: '#547590',
  shadow: '0 8px 20px rgba(46, 60, 70, 0.08)',
  headerBorderRadius: 18,
};

const getDaysRemainingLabel = (project: ProjectOverviewData) => {
  if (!project.endDate) {
    return '未设置结束日期';
  }

  if (project.daysRemaining > 0) {
    return `剩余 ${project.daysRemaining} 天`;
  }

  if (project.daysRemaining === 0) {
    return '今日到期';
  }

  return `已超期 ${Math.abs(project.daysRemaining)} 天`;
};

export interface CollapsibleProjectListProps {
  projects: ProjectOverviewData[];
  onViewDetails?: (project: ProjectOverviewData) => void;
  onExport?: (project: ProjectOverviewData) => void;
  onAddData?: (project: ProjectOverviewData) => void;
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin />, text: '进行中' },
  '已完成': { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined />, text: '待开始' },
  '已暂停': { color: 'warning', icon: <PauseCircleOutlined />, text: '已暂停' }
};

export const CollapsibleProjectList: React.FC<CollapsibleProjectListProps> = ({
  projects,
  onViewDetails,
  onExport,
  onAddData
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const formatMoney = (value: number): string => {
    if (value >= 10000) {
      return `¥${(value / 10000).toFixed(1)}万`;
    }
    return `¥${value.toLocaleString()}`;
  };

  const renderPanelHeader = (project: ProjectOverviewData) => {
    const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG['待开始'];
    const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);

    return (
      <Tooltip title={`预算使用率: ${budgetColors.percentage.toFixed(1)}% (${budgetColors.statusText})`}>
        <div style={{ width: '100%', paddingRight: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* 项目编码 */}
              <div
                style={{
                  display: 'inline-block',
                  color: LIST_THEME.accentBlue,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  background: 'rgba(84, 117, 144, 0.1)',
                  padding: '2px 6px',
                  borderRadius: 4,
                  marginBottom: 4,
                  textTransform: 'uppercase'
                }}
              >
                {(project as any).projectCode || 'UNSET'}
              </div>
              {/* 项目名称 */}
              <Text strong style={{ fontSize: 15, display: 'block', color: LIST_THEME.textPrimary }}>
                {project.name}
              </Text>
            </div>
            <Space size={8}>
              <Tag
                color={statusConfig.color}
                style={{
                  fontSize: 11,
                  marginRight: 0,
                  borderRadius: 999,
                  background: statusConfig.color === 'processing' ? 'rgba(84, 117, 144, 0.14)' :
                             statusConfig.color === 'success' ? 'rgba(101, 214, 184, 0.14)' :
                             statusConfig.color === 'warning' ? 'rgba(246, 196, 83, 0.14)' :
                             'rgba(102, 121, 134, 0.12)',
                  border: 'none'
                }}
              >
                {statusConfig.text}
              </Tag>
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: 8,
                  background: 'rgba(84, 117, 144, 0.12)',
                  color: LIST_THEME.accentBlue,
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                {project.progress}%
              </div>
            </Space>
          </div>
          <Progress
            percent={project.progress}
            size="small"
            strokeColor={project.progress >= 100 ? '#52c41a' : LIST_THEME.accentBlue}
            trailColor="rgba(16, 27, 35, 0.08)"
            showInfo={false}
            style={{ marginBottom: 0 }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderPanelContent = (project: ProjectOverviewData) => {
    const health = DashboardService.calculateProjectHealth(project.budget, project.actualCost);
    const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);

    return (
      <div style={{ padding: '4px 0' }}>
        {/* 预算使用区块 */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text style={{ fontSize: 12, color: LIST_THEME.textSecondary }}>预算使用率</Text>
            <Tag
              color={budgetColors.status === 'over' ? 'error' : budgetColors.status === 'danger' ? 'warning' : 'default'}
              style={{ fontSize: 10, borderRadius: 999, marginRight: 0 }}
            >
              {budgetColors.statusText}
            </Tag>
          </div>
          <div style={{ marginTop: 6 }}>
            <Progress
              percent={Math.min(health.percentage, 100)}
              size="small"
              strokeColor={health.color}
              trailColor="rgba(16, 27, 35, 0.08)"
              format={() => <span style={{ color: health.color, fontSize: 12, fontWeight: 600 }}>{health.percentage.toFixed(1)}%</span>}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 6, gap: 8 }}>
              <Text style={{ color: LIST_THEME.textSecondary }}>成本 {formatMoney(project.actualCost)}</Text>
              <Text style={{ color: LIST_THEME.textSecondary }}>预算 {project.budget > 0 ? formatMoney(project.budget) : '未设置'}</Text>
            </div>
          </div>
        </div>

        {/* 统计信息行 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <Space size={4}>
            <TeamOutlined style={{ color: LIST_THEME.accentBlue, fontSize: 12 }} />
            <Text style={{ fontSize: 12, color: LIST_THEME.textSecondary }}>{project.teamSize} 条记录</Text>
          </Space>
          <Space size={4}>
            <CalendarOutlined style={{ color: LIST_THEME.accentGold, fontSize: 12 }} />
            <Text style={{ fontSize: 12, color: LIST_THEME.textSecondary }}>{getDaysRemainingLabel(project)}</Text>
          </Space>
        </div>

        {/* 最近更新 */}
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 12, color: LIST_THEME.textMuted }}>最近更新：{project.recentActivity || '暂无记录'}</Text>
        </div>

        <Divider style={{ margin: '8px 0', borderColor: 'rgba(16, 27, 35, 0.08)' }} />

        {/* 操作按钮行 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, color: LIST_THEME.textPrimary, fontSize: 13 }}
            onClick={() => onViewDetails?.(project)}
          >
            查看详情 <RightOutlined style={{ fontSize: 10 }} />
          </Button>
          <Space size={6}>
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={() => onExport?.(project)}
              style={{ color: LIST_THEME.textSecondary, fontSize: 12 }}
            >
              导出
            </Button>
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => onAddData?.(project)}
              style={{ color: LIST_THEME.accentGold, fontSize: 12 }}
            >
              添加
            </Button>
          </Space>
        </div>
      </div>
    );
  };

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={(keys) => setActiveKeys(keys as string[])}
      expandIconPosition="end"
      ghost
      style={{ background: 'transparent' }}
    >
      {projects.map((project) => {
        const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);
        return (
          <Panel
            key={project.id || project._id || project.name}
            header={renderPanelHeader(project)}
            style={{
              marginBottom: 10,
              background: LIST_THEME.panelBg,
              border: `1px solid ${LIST_THEME.panelBorder}`,
              borderRadius: LIST_THEME.headerBorderRadius,
              boxShadow: LIST_THEME.shadow,
              overflow: 'hidden',
              transition: 'box-shadow 0.2s ease'
            }}
            headerStyle={{
              background: LIST_THEME.headerBg,
              padding: '14px 16px'
            }}
          >
            <div style={{ padding: '16px 16px 14px' }}>
              {renderPanelContent(project)}
            </div>
          </Panel>
        );
      })}
    </Collapse>
  );
};

export default CollapsibleProjectList;